import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { X, Copy, Check, Star, Mail, ExternalLink, Loader2 } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';

export default function ReviewModal({ bon, onClose }) {
  const { t } = useT();
  const { klanten } = useData();
  const [reviewUrl, setReviewUrl] = useState('');
  const [qr, setQr] = useState('');
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const klant = (klanten || []).find(k => k.id === bon?.klantId);

  useEffect(() => {
    carsysApi.getReviewInstellingen()
      .then(s => setReviewUrl((s.googleReviewUrl || '').trim()))
      .catch(() => setReviewUrl(''))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!reviewUrl) { setQr(''); return; }
    QRCode.toDataURL(reviewUrl, { margin: 1, width: 320 }).then(setQr).catch(() => setQr(''));
  }, [reviewUrl]);

  const copy = () => { navigator.clipboard?.writeText(reviewUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const send = async () => {
    setSending(true);
    try {
      const res = await carsysApi.sendReviewInvite(bon.id);
      if (res.ok) toast.success(t('Reviewverzoek verstuurd', 'Reviewverzoek verstuurd'), { description: res.email });
      else toast.warning(t('Niet verstuurd', 'Niet verstuurd'), { description: res.status });
    } catch (e) {
      toast.error(t('Versturen mislukt', 'Versturen mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setSending(false); }
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }} data-testid="review-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Star size={18} color="#f59e0b" /> {t('Vraag een review', 'Vraag een review')}</h3>
          <button className="cs-btn ghost" onClick={onClose} data-testid="review-close"><X size={16} /></button>
        </div>

        {loading ? (
          <div className="cs-empty"><Loader2 size={16} className="animate-spin" style={{ verticalAlign: -3, marginRight: 6 }} />{t('Laden…', 'Laden…')}</div>
        ) : !reviewUrl ? (
          <div className="cs-empty" data-testid="review-no-url" style={{ padding: 20 }}>
            {t('Stel eerst je Google-reviewlink in bij', 'Stel eerst je Google-reviewlink in bij')} <b>{t('Instellingen → Reviews', 'Instellingen → Reviews')}</b>.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
              {t('Stuur', 'Stuur')} {klant?.naam || t('de klant', 'de klant')} {t('een e-mail, of laat de klant de QR scannen in de werkplaats.', 'een e-mail, of laat de klant de QR scannen in de werkplaats.')}
            </div>
            {qr && (
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <img src={qr} alt="Review QR" style={{ width: 180, height: 180 }} data-testid="review-qr" />
                <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 4 }}>{t('Scan om een review achter te laten', 'Scan om een review achter te laten')}</div>
              </div>
            )}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#475569' }}>{reviewUrl}</span>
              <button onClick={copy} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#2563eb', display: 'flex' }} data-testid="review-copy">{copied ? <Check size={15} /> : <Copy size={15} />}</button>
              <a href={reviewUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', display: 'flex' }} data-testid="review-open"><ExternalLink size={15} /></a>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="cs-btn" onClick={onClose}>{t('Sluiten', 'Sluiten')}</button>
              <button className="cs-btn primary" onClick={send} disabled={sending} data-testid="review-send">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} {t('Stuur per e-mail', 'Stuur per e-mail')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
